import React, { useState } from 'react';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/Info';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownload';

export default function Register({ onBack, selectedRole }) {
  const [role, setRole] = useState(selectedRole || 'patient');
  const [step, setStep] = useState(1);
  const [patientUid, setPatientUid] = useState('');
  const [form, setForm] = useState({
    name: '', phone: '', aadhaarNumber: '', aadhaarLast4: '',
    dateOfBirth: '', password: '', specialization: '',
    nmcNumber: '', providerRole: 'hospital', registrationNumber: ''
  });
  const [certificate, setCertificate] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [agreed, setAgreed] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleGenerateOtp = async () => {
    if (!form.name) { setError('Please enter your full name.'); return; }
    if (!form.phone || form.phone.length < 10) { setError('Please enter a valid phone number.'); return; }
    if (role === 'patient') {
      if (!form.aadhaarNumber || form.aadhaarNumber.length !== 12) { setError('Please enter a valid 12-digit Aadhaar number.'); return; }
      if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) { setError('Please enter last 4 digits of Aadhaar.'); return; }
      if (!form.dateOfBirth) { setError('Please enter your date of birth.'); return; }
    }
    if (!form.password || form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!agreed) { setError('Please agree to the Privacy Policy.'); return; }
    setLoading(true);
    setError('');
    const mockOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(mockOtp);
    setOtp('');
    setStep(2);
    setLoading(false);
  };

  const handleRegister = async () => {
    if (role === 'patient' && otp !== generatedOtp) { setError('Invalid OTP. Please try again.'); return; }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('Name', form.name);
      formData.append('Phone', form.phone);
      formData.append('Password', form.password);
      formData.append('Role', role);
      if (role === 'patient') {
        formData.append('AadhaarLast4', form.aadhaarLast4);
        formData.append('DateOfBirth', form.dateOfBirth);
      }
      if (role === 'doctor') {
        formData.append('Specialization', form.specialization);
        formData.append('NmcNumber', form.nmcNumber);
        if (certificate) formData.append('Certificate', certificate);
      }
      if (role === 'provider') {
        formData.append('ProviderRole', form.providerRole);
        formData.append('RegistrationNumber', form.registrationNumber);
        if (docFile) formData.append('Document', docFile);
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Auth/register`, { method: 'POST', body: formData });
      const data = await response.json();
      if (response.ok) {
        if (role === 'patient') {
          setPatientUid(data.patientUid || '');
          setSuccess('Your MyHealth Journal ID has been created');
        } else {
          setSuccess('Your account has been submitted for verification.');
        }
        setStep(3);
      } else {
        setError(data.message);
      }
    } catch { setError('Cannot connect to server.'); }
    setLoading(false);
  };

  const roleCards = [
    { value: 'patient', icon: <PersonIcon style={{ fontSize: '32px' }} />, label: 'Patient' },
    { value: 'doctor', icon: <MedicalServicesIcon style={{ fontSize: '32px' }} />, label: 'Doctor' },
    { value: 'provider', icon: <LocalHospitalIcon style={{ fontSize: '32px' }} />, label: 'Provider' },
  ];

  // Step 2 — OTP Verification Screen
  if (step === 2 && role === 'patient') {
    return (
      <div style={styles.otpPage}>
        <div style={styles.otpBlob1} />
        <div style={styles.otpBlob2} />
        <div style={styles.otpBrandRow}>
          <VerifiedUserIcon style={{ fontSize: '20px', color: '#003d9b' }} />
          <span style={styles.otpBrandName}>MyHealth Journal</span>
        </div>
        <div style={styles.otpCard}>
          <div style={styles.otpIconBox}>
            <LockOutlinedIcon style={{ fontSize: '32px', color: '#003d9b' }} />
          </div>
          <h2 style={styles.otpTitle}>Verify Your Identity</h2>
          <p style={styles.otpSubtitle}>
            Enter the 6-digit OTP sent to your Aadhaar-linked mobile number:{' '}
            <strong>+91 ••••• ••{form.phone.slice(-3)}</strong>
          </p>
          <div style={styles.otpSimBox}>
            <p style={styles.otpSimLabel}>Your OTP (simulated — Setu integration pending):</p>
            <p style={styles.otpSimCode}>{generatedOtp}</p>
          </div>
          <div style={styles.otpInputRow}>
            {[0,1,2,3,4,5].map(i => (
              <input
                key={i}
                id={`otp-${i}`}
                style={{ ...styles.otpDigitInput, borderColor: otp[i] ? '#003d9b' : '#c3c6d6', borderWidth: otp[i] ? '2px' : '1px' }}
                type="text"
                maxLength={1}
                value={otp[i] || ''}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  const chars = otp.split('');
                  chars[i] = val;
                  setOtp(chars.join(''));
                  if (val && i < 5) document.getElementById(`otp-${i+1}`)?.focus();
                }}
                onKeyDown={e => {
                  if (e.key === 'Backspace' && !otp[i] && i > 0) {
                    const chars = otp.split('');
                    chars[i-1] = '';
                    setOtp(chars.join(''));
                    document.getElementById(`otp-${i-1}`)?.focus();
                  }
                }}
              />
            ))}
          </div>
          {error && <div style={styles.errorBox}>{error}</div>}
          <button
            style={{ ...styles.submitBtn, marginTop: '8px', opacity: loading ? 0.8 : 1 }}
            onClick={() => { if (otp !== generatedOtp) { setError('Invalid OTP.'); } else { handleRegister(); } }}
            disabled={loading || otp.length < 6}
          >
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={styles.signinText}>
              Didn't receive the code?{' '}
              <span style={styles.link} onClick={() => {
                const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
                setGeneratedOtp(newOtp);
                setOtp('');
                setError('');
              }}>Resend OTP</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 3 — Success Screen
  if (step === 3) {
    return (
      <div style={styles.otpPage}>
        <div style={styles.otpBlob1} />
        <div style={styles.otpBlob2} />
        <div style={styles.successCard}>
          {/* Brand */}
          <div style={styles.successBrandRow}>
            <div style={styles.successBrandIcon}>
              <MedicalServicesIcon style={{ fontSize: '32px', color: '#0052CC' }} />
            </div>
            <p style={styles.successBrandName}>MyHealth Journal</p>
          </div>

          {/* Check icon */}
          <div style={styles.successCheckBox}>
            <CheckCircleOutlineIcon style={{ fontSize: '40px', color: role === 'patient' ? '#16a34a' : '#0d9488' }} />
          </div>

          {/* Title */}
          <h2 style={styles.successTitle}>
            {role === 'patient' ? 'Account Created!' : 'Registration Submitted'}
          </h2>

          {/* Badge */}
          <div style={styles.successBadge}>
            {role === 'patient' ? 'Your MyHealth Journal ID has been created' : 'Your credentials have been received'}
          </div>

          <p style={styles.successSubtitle}>
            Welcome to a more secure and connected health journey.
          </p>

          {/* Patient UID Box */}
          {role === 'patient' && patientUid && (
            <div style={styles.uidDashedBox}>
              <p style={styles.uidDashedLabel}>PATIENT UID</p>
              <p style={styles.uidDashedCode}>{patientUid}</p>
            </div>
          )}

          {/* Doctor/Provider verification info box */}
          {role !== 'patient' && (
            <div style={styles.verificationBox}>
              <p style={styles.verificationText}>
                Your registration has been submitted successfully. Our team is verifying your details. Please log in after 30 minutes using your registered phone number and password.
              </p>
            </div>
          )}

          {/* Patient info note */}
          {role === 'patient' && (
            <div style={styles.infoRow}>
              <InfoOutlinedIcon style={{ fontSize: '18px', color: '#64748b', flexShrink: 0, marginTop: '1px' }} />
              <p style={styles.infoText}>
                <strong>Save this ID</strong> — hospitals and labs will use it to upload your reports directly to your journal.
              </p>
            </div>
          )}

          {/* Verification time note for doctor/provider */}
          {role !== 'patient' && (
            <div style={styles.timeNote}>
              <InfoOutlinedIcon style={{ fontSize: '16px', color: '#94a3b8', marginRight: '6px' }} />
              <span style={styles.timeNoteText}>Verification usually takes 30 minutes.</span>
            </div>
          )}

          {/* Go to Login */}
          <button style={styles.submitBtn} onClick={onBack}>Go to Login</button>

          {/* Download ID Card for patient */}
          {role === 'patient' && (
            <div style={{ textAlign: 'center' }}>
              <span style={styles.downloadLink}>
                <FileDownloadOutlinedIcon style={{ fontSize: '16px', marginRight: '4px' }} />
                Download ID Card (PDF)
              </span>
            </div>
          )}

          <p style={styles.successFooter}>A confirmation email has been sent to your registered address.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <section style={styles.leftPanel}>
        <div style={styles.leftDecor1} />
        <div style={styles.leftDecor2} />
        <div style={styles.leftContent}>
          <div style={styles.logoSection}>
            <div style={styles.logoBox}>
              <MedicalServicesIcon style={{ fontSize: '48px', color: '#ffffff' }} />
            </div>
            <h1 style={styles.brandName}>MyHealth Journal</h1>
          </div>
          <div style={styles.heroSection}>
            <h2 style={styles.heroTitle}>The Future of Intelligent Healthcare</h2>
            <p style={styles.heroText}>
              A secure AI-powered healthcare platform that connects patients, doctors, and providers through trusted identity verification, intelligent medical insights, and seamless health record management.
            </p>
          </div>
        </div>
        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <SecurityIcon style={{ fontSize: '28px', color: '#ffffff', marginBottom: '8px' }} />
            <h3 style={styles.featureTitle}>Trusted Identity</h3>
            <p style={styles.featureText}>Secure Aadhaar verification powered by Setu for trusted access across the healthcare ecosystem.</p>
          </div>
          <div style={styles.featureCard}>
            <SmartToyIcon style={{ fontSize: '28px', color: '#ffffff', marginBottom: '8px' }} />
            <h3 style={styles.featureTitle}>AI Health Assistant</h3>
            <p style={styles.featureText}>AI-powered summaries, medical insights, and intelligent healthcare workflows for every user.</p>
          </div>
        </div>
        <div style={styles.leftFooter}>
          <p style={styles.footerText}>© 2024 MyHealth Journal. HIPAA & GDPR Compliant.</p>
        </div>
      </section>

      <section style={styles.rightPanel}>
        <div style={styles.formContainer}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Create your account</h2>
            <p style={styles.formSubtitle}>Join thousands of clinical professionals and patients.</p>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Account Type</label>
            <div style={styles.roleGrid}>
              {roleCards.map(r => (
                <div key={r.value}
                  style={{ ...styles.roleCard, ...(role === r.value ? styles.roleCardActive : {}) }}
                  onClick={() => setRole(r.value)}>
                  <div style={{ color: role === r.value ? '#003d9b' : '#737685', marginBottom: '4px' }}>{r.icon}</div>
                  <span style={{ ...styles.roleLabel, color: role === r.value ? '#003d9b' : '#434654' }}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.twoCol}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Full name {role === 'doctor' ? '[as per NMC certificate]' : '[as per Aadhaar card]'}</label>
              <input style={styles.input} name="name" placeholder="John Doe" value={form.name} onChange={handleChange} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Phone number</label>
              <input style={styles.input} name="phone" placeholder="+91 98765 43210" value={form.phone} onChange={handleChange} />
            </div>
          </div>

          {role === 'patient' && (
            <div style={styles.twoCol}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Aadhaar number [12 digits]</label>
                <input style={styles.input} name="aadhaarNumber" placeholder="0000 0000 0000" maxLength={12} value={form.aadhaarNumber} onChange={handleChange} />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Last 4 digits of Aadhaar</label>
                <input style={styles.input} name="aadhaarLast4" placeholder="0000" maxLength={4} value={form.aadhaarLast4} onChange={handleChange} />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Date of birth</label>
                <input style={styles.input} type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
              </div>
            </div>
          )}

          {role === 'doctor' && (
            <div style={styles.twoCol}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Specialization</label>
                <select style={styles.input} name="specialization" value={form.specialization} onChange={handleChange}>
                  <option value="">Select Specialization</option>
                  <option>General Physician</option>
                  <option>Cardiology</option>
                  <option>Neurology</option>
                  <option>Pediatrics</option>
                  <option>Dermatology</option>
                  <option>Orthopedic</option>
                  <option>Psychiatrist</option>
                  <option>Other</option>
                </select>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>NMC Registration Number</label>
                <input style={styles.input} name="nmcNumber" placeholder="Enter NMC number" value={form.nmcNumber} onChange={handleChange} />
              </div>
              <div style={{ ...styles.fieldGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Upload NMC Certificate (PDF/Image)</label>
                <input style={styles.fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setCertificate(e.target.files[0])} />
                <p style={styles.hint}>Upload your NMC registration certificate for admin verification</p>
              </div>
            </div>
          )}

          {role === 'provider' && (
            <div style={styles.twoCol}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Provider Type</label>
                <select style={styles.input} name="providerRole" value={form.providerRole} onChange={handleChange}>
                  <option value="hospital">Hospital</option>
                  <option value="lab">Diagnostic Lab</option>
                  <option value="clinic">Clinic</option>
                  <option value="pharmacy">Pharmacy</option>
                </select>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Registration Number</label>
                <input style={styles.input} name="registrationNumber" placeholder="NABL / Clinical Establishment number" value={form.registrationNumber} onChange={handleChange} />
              </div>
              <div style={{ ...styles.fieldGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Upload Registration Certificate (PDF/Image)</label>
                <input style={styles.fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setDocFile(e.target.files[0])} />
                <p style={styles.hint}>Upload your government registration certificate for admin verification</p>
              </div>
            </div>
          )}

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password [minimum 8 characters]</label>
            <input style={styles.input} type="password" name="password" placeholder="••••••••" minLength={8} value={form.password} onChange={handleChange} />
          </div>

          <div style={styles.agreementRow}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#003d9b', cursor: 'pointer' }} />
            <span style={styles.agreementText}>
              I agree to the <a href="#" style={styles.link}>Privacy Policy</a> and authorize medical data sharing for clinical purposes.
            </span>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <button style={{ ...styles.submitBtn, opacity: loading ? 0.8 : 1 }} onClick={role === 'patient' ? handleGenerateOtp : handleRegister} disabled={loading}>
            {loading ? 'Processing...' : role === 'patient' ? 'Verify Aadhaar & Send OTP' : 'Send for Verification'}
            {!loading && <ArrowForwardIcon style={{ fontSize: '18px', marginLeft: '8px' }} />}
          </button>

          <div style={styles.signinRow}>
            <p style={styles.signinText}>Already have an account?{' '}<span style={styles.link} onClick={onBack}>Sign In</span></p>
          </div>
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', sans-serif", overflow: 'hidden' },
  leftPanel: { width: '40%', background: '#003d9b', color: '#ffffff', padding: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden', minHeight: '100vh' },
  leftDecor1: { position: 'absolute', top: '-96px', left: '-96px', width: '256px', height: '256px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(48px)' },
  leftDecor2: { position: 'absolute', bottom: '-96px', right: '-96px', width: '384px', height: '384px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(48px)' },
  leftContent: { position: 'relative', zIndex: 10 },
  logoSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '80px' },
  logoBox: { width: '80px', height: '80px', background: 'rgba(255,255,255,0.1)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' },
  brandName: { fontSize: '48px', fontWeight: '700', color: '#ffffff', letterSpacing: '-0.02em', margin: 0, lineHeight: '56px' },
  heroSection: { marginBottom: '48px' },
  heroTitle: { fontSize: '32px', fontWeight: '600', color: '#ffffff', marginBottom: '16px', lineHeight: '40px', letterSpacing: '-0.01em' },
  heroText: { fontSize: '16px', color: 'rgba(255,255,255,0.9)', lineHeight: '24px', textAlign: 'center' },
  featureGrid: { position: 'relative', zIndex: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '48px' },
  featureCard: { background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '20px' },
  featureTitle: { fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '4px', margin: '0 0 4px' },
  featureText: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: '16px', margin: 0 },
  leftFooter: { position: 'relative', zIndex: 10, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' },
  footerText: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: 0 },
  rightPanel: { flex: 1, background: '#ffffff', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '64px 80px', overflowY: 'auto' },
  formContainer: { width: '100%', maxWidth: '640px' },
  formHeader: { marginBottom: '32px' },
  formTitle: { fontSize: '32px', fontWeight: '600', color: '#191c1e', margin: '0 0 8px', letterSpacing: '-0.01em', lineHeight: '40px' },
  formSubtitle: { fontSize: '16px', color: '#434654', margin: 0, lineHeight: '24px' },
  fieldGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: '#191c1e', marginBottom: '6px', lineHeight: '20px' },
  roleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  roleCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px 12px', border: '1px solid #c3c6d6', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s', background: '#ffffff' },
  roleCardActive: { border: '2px solid #003d9b', background: 'rgba(0,61,155,0.04)' },
  roleLabel: { fontSize: '14px', fontWeight: '600', marginTop: '4px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '4px' },
  input: { width: '100%', padding: '12px 16px', border: '1px solid #c3c6d6', borderRadius: '8px', fontSize: '16px', color: '#191c1e', outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", background: '#ffffff', lineHeight: '24px' },
  fileInput: { width: '100%', padding: '8px', border: '1px solid #c3c6d6', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', cursor: 'pointer' },
  hint: { fontSize: '12px', color: '#737685', marginTop: '4px', fontStyle: 'italic' },
  agreementRow: { display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '24px', marginTop: '8px' },
  agreementText: { fontSize: '16px', color: '#434654', lineHeight: '24px' },
  link: { color: '#003d9b', fontWeight: '600', cursor: 'pointer', textDecoration: 'none' },
  errorBox: { background: '#ffdad6', color: '#93000a', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  submitBtn: { width: '100%', padding: '16px 24px', background: '#003d9b', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Inter', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(9,30,66,0.08)', transition: 'all 0.2s', marginBottom: '24px' },
  signinRow: { textAlign: 'center' },
  signinText: { fontSize: '16px', color: '#434654', margin: 0, lineHeight: '24px' },
  otpPage: { minHeight: '100vh', background: 'linear-gradient(135deg, #eef1f6 0%, #e8ecf3 40%, #dce3ee 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif", padding: '24px', position: 'relative', overflow: 'hidden' },
  otpBlob1: { position: 'fixed', top: '-10%', right: '-5%', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(0,82,204,0.05)', filter: 'blur(100px)', pointerEvents: 'none' },
  otpBlob2: { position: 'fixed', bottom: '-10%', left: '-5%', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(0,104,123,0.04)', filter: 'blur(100px)', pointerEvents: 'none' },
  otpBrandRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '40px', zIndex: 10 },
  otpBrandName: { fontSize: '20px', fontWeight: '700', color: '#003d9b', letterSpacing: '-0.01em' },
  otpCard: { background: '#ffffff', border: '1px solid #dde1ea', borderRadius: '16px', padding: '48px 40px', maxWidth: '520px', width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'relative', zIndex: 10 },
  otpIconBox: { width: '72px', height: '72px', background: 'rgba(0,61,155,0.08)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', border: '1px solid rgba(0,61,155,0.1)' },
  otpTitle: { fontSize: '28px', fontWeight: '700', color: '#0a0f1e', margin: '0 0 12px', letterSpacing: '-0.02em' },
  otpSubtitle: { fontSize: '15px', color: '#64748b', margin: '0 0 24px', lineHeight: '24px' },
  otpSimBox: { background: '#f0f4ff', border: '1px solid #c7d7f7', borderRadius: '10px', padding: '12px 16px', marginBottom: '28px' },
  otpSimLabel: { fontSize: '12px', color: '#64748b', margin: '0 0 4px' },
  otpSimCode: { fontSize: '28px', fontWeight: '700', color: '#003d9b', letterSpacing: '8px', margin: 0, fontFamily: 'monospace' },
  otpInputRow: { display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '28px' },
  otpDigitInput: { width: '52px', height: '60px', textAlign: 'center', fontSize: '22px', fontWeight: '600', color: '#0a0f1e', border: '1px solid #c3c6d6', borderRadius: '10px', outline: 'none', fontFamily: "'Inter', sans-serif", background: '#ffffff', transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box' },
  successCard: { background: '#ffffff', border: '1px solid #dde1ea', borderRadius: '16px', padding: '48px 40px', maxWidth: '520px', width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'relative', zIndex: 10 },
  successBrandRow: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' },
  successBrandIcon: { width: '64px', height: '64px', background: 'rgba(0,82,204,0.08)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', border: '1px solid rgba(0,82,204,0.1)' },
  successBrandName: { fontSize: '20px', fontWeight: '700', color: '#003d9b', margin: 0 },
  successCheckBox: { width: '72px', height: '72px', background: 'rgba(187,247,208,0.4)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '1px solid rgba(22,163,74,0.15)' },
  successTitle: { fontSize: '24px', fontWeight: '700', color: '#0a0f1e', margin: '0 0 12px', letterSpacing: '-0.01em' },
  successBadge: { display: 'inline-block', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '6px 16px', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '12px' },
  successSubtitle: { fontSize: '15px', color: '#64748b', margin: '0 0 24px', lineHeight: '24px' },
  uidDashedBox: { border: '2px dashed #c3c6d6', borderRadius: '12px', padding: '24px', marginBottom: '20px', background: '#f8f9fb' },
  uidDashedLabel: { fontSize: '11px', fontWeight: '700', color: '#64748b', letterSpacing: '0.12em', margin: '0 0 8px', textTransform: 'uppercase' },
  uidDashedCode: { fontSize: '28px', fontWeight: '700', color: '#003d9b', letterSpacing: '2px', margin: 0, fontFamily: 'monospace', lineHeight: '36px' },
  verificationBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px', textAlign: 'center' },
  verificationText: { fontSize: '15px', color: '#475569', lineHeight: '24px', margin: 0 },
  timeNote: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' },
  timeNoteText: { fontSize: '13px', color: '#94a3b8' },
  infoRow: { display: 'flex', gap: '10px', alignItems: 'flex-start', textAlign: 'left', background: '#f8fafc', borderRadius: '10px', padding: '14px 16px', marginBottom: '28px' },
  infoText: { fontSize: '14px', color: '#475569', lineHeight: '22px', margin: 0 },
  downloadLink: { display: 'inline-flex', alignItems: 'center', fontSize: '14px', color: '#003d9b', fontWeight: '600', cursor: 'pointer', textDecoration: 'none' },
  successFooter: { fontSize: '13px', color: '#94a3b8', marginTop: '24px', marginBottom: 0 },
};